import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Text, Button, Group, TextInput, Switch, Select, Modal, Stack, Table, ActionIcon, Tooltip, Badge, Center, Loader, Card, Menu } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { useBreakpoint } from '../../hooks/useBreakpoint'
import { notifications } from '@mantine/notifications'
import { modals } from '@mantine/modals'
import { IconPlus, IconEdit, IconTrash, IconSearch, IconListCheck, IconDotsVertical } from '@tabler/icons-react'
import { getChecklistItems, createChecklistItem, updateChecklistItem, deleteChecklistItem } from '../../api/client'

const CATEGORIES = ['Hardware','Storage','Security','Network','General','Software','Cleaning','Other']

export default function ChecklistItemsTab() {
  const { t } = useTranslation()
  const { isMobile } = useBreakpoint()
  const [items, setItems] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [opened, { open, close }] = useDisclosure(false)
  const [editing, setEditing] = useState<any>(null)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({ name:'', category:'', description:'', requiresNote:false })

  const load = async () => { setLoading(true); try { const r = await getChecklistItems(); setItems(r.data??[]) } catch{} finally{setLoading(false)} }
  useEffect(() => { load() }, [])

  const openCreate = () => { setEditing(null); setForm({name:'',category:'',description:'',requiresNote:false}); open() }
  const openEdit   = (i: any) => { setEditing(i); setForm({name:i.name,category:i.category??'',description:i.description??'',requiresNote:i.requiresNote??false}); open() }

  const save = async () => {
    if (!form.name.trim()) { notifications.show({color:'red',message:'Please enter an item name'}); return }
    setSaving(true)
    try {
      editing ? await updateChecklistItem(editing.id, form) : await createChecklistItem(form)
      notifications.show({color:'teal',message: editing?'Updated successfully':'Item added successfully'})
      close(); load()
    } catch { notifications.show({color:'red',message:'An error occurred'}) } finally { setSaving(false) }
  }

  const handleDelete = (item: any) => modals.openConfirmModal({
    title:'Delete Item', children:<Text size="sm">Confirm delete <b>{item.name}</b>?</Text>,
    labels:{confirm:'Delete',cancel:'Cancel'}, confirmProps:{color:'red'},
    onConfirm: async () => { await deleteChecklistItem(item.id); load() },
  })

  const filtered = items.filter(i => !search || i.name?.toLowerCase().includes(search.toLowerCase()) || i.category?.toLowerCase().includes(search.toLowerCase()))

  return (
    <Box>
      <Group justify="space-between" mb="md" wrap="wrap">
        <Group gap={8}><IconListCheck size={18} color="#10b981"/><Text fw={600}>Checklist Items <Badge color="teal" variant="light" size="sm">{items.length}</Badge></Text></Group>
        <Group gap={8} wrap="wrap">
          <TextInput size="xs" placeholder="Search..." value={search} onChange={e=>setSearch(e.target.value)} leftSection={<IconSearch size={13}/>} style={{width: isMobile ? '100%' : 200}}/>
          <Button size="xs" leftSection={<IconPlus size={13}/>} color="teal" onClick={openCreate}>{t('common.add', 'Add')} Item</Button>
        </Group>
      </Group>
      <Box style={{background:'#fff',borderRadius:12,border:'1px solid #e2e8f0',overflow:'auto'}}>
        {loading ? <Center p="xl"><Loader size="sm" color="teal"/></Center> : isMobile ? (
          filtered.length === 0 ? (
            <Center p="xl"><Text size="sm" c="dimmed">No items yet</Text></Center>
          ) : (
            <Stack gap="xs" p="xs">
              {filtered.map(item => (
                <Card key={item.id} withBorder radius="lg" p="md">
                  <Group justify="space-between" align="flex-start" wrap="nowrap" gap="xs">
                    <Box style={{ minWidth: 0, flex: 1 }}>
                      <Text size="sm" fw={600}>{item.name}</Text>
                      {item.description && <Text size="xs" c="dimmed" lineClamp={1}>{item.description}</Text>}
                      <Group gap={6} mt={4}>
                        <Badge color="violet" variant="light" size="xs">{item.category || '-'}</Badge>
                        <Badge color={item.requiresNote ? 'orange' : 'gray'} variant="light" size="xs">{item.requiresNote ? 'Required' : 'Not Required'}</Badge>
                      </Group>
                    </Box>
                    <Menu shadow="md" width={160} position="bottom-end">
                      <Menu.Target>
                        <ActionIcon variant="subtle" color="gray" size="lg" style={{ flexShrink: 0 }}><IconDotsVertical size={18} /></ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item leftSection={<IconEdit size={15} />} onClick={() => openEdit(item)}>{t('common.edit', 'Edit')}</Menu.Item>
                        <Menu.Item leftSection={<IconTrash size={15} />} color="red" onClick={() => handleDelete(item)}>{t('common.delete', 'Delete')}</Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </Group>
                </Card>
              ))}
            </Stack>
          )
        ) : (
          <Table highlightOnHover>
            <Table.Thead style={{background:'#f8fafc'}}>
              <Table.Tr>{['Item Name','Category','Requires Remark',''].map(h=>(
                <Table.Th key={h} style={{fontSize:11,color:'#94a3b8',fontWeight:500,textTransform:'uppercase'}}>{h}</Table.Th>
              ))}</Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {filtered.length===0 ? <Table.Tr><Table.Td colSpan={4}><Center p="xl"><Text size="sm" c="dimmed">No items yet</Text></Center></Table.Td></Table.Tr>
              : filtered.map(item=>(
                <Table.Tr key={item.id}>
                  <Table.Td><Text size="sm" fw={600}>{item.name}</Text>{item.description&&<Text size="xs" c="dimmed">{item.description}</Text>}</Table.Td>
                  <Table.Td><Badge color="violet" variant="light" size="sm">{item.category||'-'}</Badge></Table.Td>
                  <Table.Td><Badge color={item.requiresNote?'orange':'gray'} variant="light" size="xs">{item.requiresNote?'Required':'Not Required'}</Badge></Table.Td>
                  <Table.Td><Group gap={4} justify="flex-end">
                    <Tooltip label={t('common.edit', 'Edit')}><ActionIcon size="sm" variant="subtle" color="blue" onClick={()=>openEdit(item)}><IconEdit size={14}/></ActionIcon></Tooltip>
                    <Tooltip label={t('common.delete', 'Delete')}><ActionIcon size="sm" variant="subtle" color="red" onClick={()=>handleDelete(item)}><IconTrash size={14}/></ActionIcon></Tooltip>
                  </Group></Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Box>
      <Modal opened={opened} onClose={close} title={<Text fw={600}>{editing?'Edit Item':'Add Item'}</Text>} size="md" radius="lg" fullScreen={isMobile}>
        <Stack gap="sm">
          <TextInput label="Item Name *" value={form.name} onChange={e=>setForm(p=>({...p,name:e.target.value}))} placeholder="e.g. Clean Dust, Check Disk C & D"/>
          <Select label="Category" data={CATEGORIES} value={form.category||null} onChange={v=>setForm(p=>({...p,category:v??''}))} clearable searchable/>
          <TextInput label="Description" value={form.description} onChange={e=>setForm(p=>({...p,description:e.target.value}))} placeholder="Inspection method or notes"/>
          <Switch label="Requires Remark when Failed" checked={form.requiresNote} onChange={e=>setForm(p=>({...p,requiresNote:e.currentTarget.checked}))} color="orange"/>
          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={close}>{t('common.cancel', 'Cancel')}</Button>
            <Button color="teal" loading={saving} onClick={save}>{editing?'Save':'Add'}</Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  )
}
